// ApprovalEditModal — Rule 2 / Rule 3 partial-approval UI.
//
// One modal, four call sites:
//   - Player recharge approve     (R from rr.amount; agent debits A)
//   - Player withdrawal mark-paid (R from wr.amount; agent +A, player +(R-A))
//   - Sub-agent recharge approve  (parent debits A)
//   - Sub-agent withdrawal mark-paid (parent +A, sub-agent +(R-A))
//
// Validation:
//   - approved ∈ [1, requested]
//   - if approved < requested: note required, ≥5 chars
//   - withdrawal mark-paid requires paid_transaction_id (≥6 chars)
//
// Live preview shows the refund amount on partial withdrawal so the
// agent understands the math before they confirm.

import { FormEvent, useEffect, useMemo, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';

type Kind = 'recharge' | 'withdrawal';

export type ApprovalEditModalProps = {
  open: boolean;
  kind: Kind;
  requestedAmount: number;        // R
  counterpartyLabel: string;      // "@username" — player or sub-agent
  agentBalance?: number;          // recharge only — for solvency hint
  onCancel: () => void;
  onConfirm: (args: {
    approvedAmount: number;
    note?: string;
    paidTransactionId?: string;
    // Phase 18 — withdrawal flows can ship a payout-proof image in
    // the SAME confirm action. Parent component handles the
    // mark-paid call then chains the proof upload if blob is set.
    // Recharge flows ignore this field.
    payoutProofBlob?: Blob | null;
  }) => Promise<void>;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function ApprovalEditModal(props: ApprovalEditModalProps) {
  const {
    open, kind, requestedAmount, counterpartyLabel,
    agentBalance, onCancel, onConfirm,
  } = props;

  const [amountStr, setAmountStr]   = useState<string>('');
  const [note, setNote]             = useState<string>('');
  const [txn, setTxn]                 = useState<string>('');
  const [payoutProofBlob, setPayoutProofBlob] = useState<Blob | null>(null);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState<string | null>(null);

  // Reset / pre-fill on open.
  useEffect(() => {
    if (!open) return;
    setAmountStr(String(requestedAmount));
    setNote('');
    setTxn('');
    setPayoutProofBlob(null);
    setErr(null);
    setBusy(false);
  }, [open, requestedAmount]);

  const amount = Math.round(Number(amountStr));
  const isPartial = Number.isFinite(amount) && amount < requestedAmount;
  const refund = Math.max(0, requestedAmount - (Number.isFinite(amount) ? amount : 0));
  const isValid = useMemo(() => {
    if (!Number.isFinite(amount) || amount < 1) return false;
    if (amount > requestedAmount) return false;
    if (isPartial && note.trim().length < 5) return false;
    if (kind === 'withdrawal' && txn.trim().length < 6) return false;
    if (kind === 'recharge' && agentBalance != null && amount > agentBalance) return false;
    return true;
  }, [amount, requestedAmount, isPartial, note, kind, txn, agentBalance]);

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!Number.isFinite(amount) || amount < 1) {
      setErr('Approved amount must be at least ₹1'); return;
    }
    if (amount > requestedAmount) {
      setErr(`Approved amount cannot exceed requested ${inr(requestedAmount)}`); return;
    }
    if (isPartial && note.trim().length < 5) {
      setErr('Reason (≥5 chars) is required when approving a different amount'); return;
    }
    if (kind === 'withdrawal' && txn.trim().length < 6) {
      setErr('UPI transaction ID is required (≥6 chars)'); return;
    }
    setBusy(true);
    try {
      await onConfirm({
        approvedAmount: amount,
        note: note.trim() || undefined,
        paidTransactionId: kind === 'withdrawal' ? txn.trim() : undefined,
        // Phase 18 — only forward the blob on withdrawal flows.
        payoutProofBlob: kind === 'withdrawal' ? payoutProofBlob : null,
      });
    } catch (e: any) {
      setErr(e?.message || 'Failed — try again');
    } finally {
      setBusy(false);
    }
  }

  const tone = kind === 'recharge' ? 'emerald' : 'rose';
  const title = kind === 'recharge'
    ? `Approve recharge from ${counterpartyLabel}`
    : `Mark withdrawal paid for ${counterpartyLabel}`;
  const insufficient = kind === 'recharge'
    && agentBalance != null
    && Number.isFinite(amount)
    && amount > agentBalance;

  return (
    <div
      className="fixed inset-0 z-[8000] grid place-items-start sm:place-items-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={busy ? undefined : onCancel}
      role="dialog" aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-3xl
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                   shadow-[0_20px_60px_rgba(0,0,0,0.35)]
                   overflow-hidden my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-5 py-4 border-b ${
          tone === 'emerald'
            ? 'border-emerald-200/40 dark:border-emerald-500/25 bg-emerald-50/40 dark:bg-emerald-900/10'
            : 'border-rose-200/40 dark:border-rose-500/25 bg-rose-50/40 dark:bg-rose-900/10'
        }`}>
          <h3 className="text-[15px] font-bold text-neutral-900 dark:text-white">
            {title}
          </h3>
          <div className="text-[12px] text-arena-700 dark:text-white/60 mt-0.5">
            Requested: <span className="font-mono font-semibold tabular-nums">{inr(requestedAmount)}</span>
          </div>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.14em] font-bold text-arena-600 dark:text-neon-200/70 mb-1">
              Approve amount (₹)
            </label>
            <input
              type="number" min={1} max={requestedAmount}
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              className="w-full rounded-xl px-3 py-2 text-[14px] font-mono tabular-nums
                         bg-arena-50/40 dark:bg-arena-700/40
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/30
                         text-neutral-900 dark:text-white"
              autoFocus
            />
            <div className="mt-1 text-[11px] text-arena-600 dark:text-neon-200/70">
              Cannot exceed requested {inr(requestedAmount)}.
            </div>
          </div>

          {/* Partial-approval refund preview (withdrawal only) */}
          {kind === 'withdrawal' && isPartial && refund > 0 && (
            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/30 px-3 py-2.5 text-[12.5px] text-amber-800 dark:text-amber-200">
              <Icon name="info" size={13} className="inline-block mr-1 mb-0.5" />
              <span className="font-mono font-bold tabular-nums">{inr(refund)}</span>
              {' '}will be refunded to {counterpartyLabel}'s wallet automatically.
            </div>
          )}
          {kind === 'recharge' && isPartial && (
            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/30 px-3 py-2.5 text-[12.5px] text-amber-800 dark:text-amber-200">
              <Icon name="info" size={13} className="inline-block mr-1 mb-0.5" />
              Partial approval. Any attached bonus will be auto-rejected.
            </div>
          )}

          {/* Reason (required on partial) */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.14em] font-bold text-arena-600 dark:text-neon-200/70 mb-1">
              Reason {isPartial ? <span className="text-rose-600 dark:text-rose-300">(required, ≥5 chars)</span> : <span className="text-arena-400">(optional)</span>}
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isPartial ? 'e.g. Player only paid 30 rupees' : 'Optional note'}
              className="w-full rounded-xl px-3 py-2 text-[14px]
                         bg-arena-50/40 dark:bg-arena-700/40
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/30
                         text-neutral-900 dark:text-white"
            />
          </div>

          {/* UPI txn id (withdrawal only — required) */}
          {kind === 'withdrawal' && (
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] font-bold text-arena-600 dark:text-neon-200/70 mb-1">
                UPI transaction ID
              </label>
              <input
                type="text" value={txn} onChange={(e) => setTxn(e.target.value)}
                placeholder="e.g. 412312345678"
                className="w-full rounded-xl px-3 py-2 text-[14px] font-mono
                           bg-arena-50/40 dark:bg-arena-700/40
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                           focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/30
                           text-neutral-900 dark:text-white"
              />
            </div>
          )}

          {/* Phase 18 — payout-proof screenshot (withdrawal only).
              OPTIONAL on submit; if attached, the parent component
              chains an upload call to /payout-proof immediately
              after the mark-paid endpoint succeeds. If skipped,
              admin/agent has 24h to attach from the queue card. */}
          {kind === 'withdrawal' && (
            <ProofImagePicker
              onFile={(b) => setPayoutProofBlob(b)}
              onClear={() => setPayoutProofBlob(null)}
              onError={(m) => setErr(m)}
              label="Upload payout screenshot (optional)"
              hint="Recipient sees this with a 2-view limit. You can also attach it from History within 24 h."
            />
          )}

          {/* Solvency check (recharge only) */}
          {insufficient && (
            <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-500/30 px-3 py-2 text-[12.5px] text-rose-700 dark:text-rose-200">
              Your pool ({inr(agentBalance!)}) cannot cover this approval. Top up first.
            </div>
          )}
          {err && (
            <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-500/30 px-3 py-2 text-[12.5px] text-rose-700 dark:text-rose-200">
              {err}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button" onClick={onCancel} disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-full px-4 py-2.5 text-[13px] font-semibold transition active:scale-95 disabled:opacity-60
                         bg-arena-100/60 dark:bg-arena-700/50 hover:bg-arena-200/70 dark:hover:bg-arena-700/70
                         text-arena-700 dark:text-neon-200/85 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={busy || !isValid}
              className={`flex-1 inline-flex items-center justify-center gap-1 rounded-full px-4 py-2.5 text-[13px] font-bold transition active:scale-95 disabled:opacity-50
                          ${tone === 'emerald'
                            ? 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white shadow-[0_2px_10px_rgba(16,185,129,0.35)]'
                            : 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white shadow-[0_2px_10px_rgba(244,63,94,0.35)]'}`}
            >
              {busy
                ? <Icon name="progress_activity" size={14} className="animate-spin" />
                : <Icon name="check" size={14} />}
              {busy
                ? 'Submitting…'
                : kind === 'recharge' ? 'Approve' : 'Mark paid'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
